/** @prettier */

import {SnackbarProvider, VariantType, useSnackbar} from 'notistack';
import {useLoaderData, useParams, LoaderFunctionArgs} from 'react-router-dom';
import {useState, useEffect} from 'react';
import {Grid, Container, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsSitesBySiteID,
  patchCompositionsSites,
} from '../../API/API_Collection';
//type import
import * as apiIF from '../../Type/api';

import {limit} from '../../define';

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<
  apiIF.res.getCompositionsSitesBySiteID | undefined
> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }
  try {
    const response = await getCompositionsSitesBySiteID(params.siteID);

    //admin以外がアクセスしたらリダイレクト
    if (response['site-authority'] !== 'admin') {
      throw new Error('Insufficient permission');
    }

    return response;
  } catch {
    return undefined;
  }
}

const Main = () => {
  const trans = useTranslation().t; //翻訳関数
  const {enqueueSnackbar} = useSnackbar();
  const loaderData = useLoaderData() as
    | apiIF.res.getCompositionsSitesBySiteID
    | undefined;
  //パスパラメータ取得
  const {siteID} = useParams();

  //prettier-ignore
  const [serviceHistory, setServiceHistory] = useState<apiIF.res.getCompositionsSitesBySiteID['service-history']>(''); //テキストボックスの中身
  const [isOver, setIsOver] = useState(false); // ボタンの操作、警告文フラグ

  useEffect(() => {
    if (loaderData !== undefined) {
      setServiceHistory(loaderData['service-history']);
    }
  }, [loaderData]);

  // テキストボックスのバイト数チェック
  useEffect(() => {
    setIsOver(
      getBytes(serviceHistory) > limit.SITE_INFO_NUM_SERVICE_HISTORY_TEXT_MAX
    );
  }, [serviceHistory]);

  /**
   * テキストのバイト数チェック用
   * @param str テキスト
   * @returns number テキストのバイト数
   */
  const getBytes = (str: string) => {
    return encodeURIComponent(str).replace(/%../g, 'x').length;
  };

  /**
   * テキストを保存します
   */
  const saveSiteInfo = async () => {
    if (siteID === undefined) return;

    // 保存用のデータ型に変換
    const tempSiteInfo: apiIF.reqParameters.patchCompositionsSites = {
      'service-history': serviceHistory,
    };
    try {
      const promise = patchCompositionsSites(siteID, tempSiteInfo);
      if ((await promise).result === 'OK') {
        const variant: VariantType = 'success';
        enqueueSnackbar(trans('ServiceHistory.success'), {variant});
      }
    } catch {
      const variant: VariantType = 'error';
      enqueueSnackbar(trans('ServiceHistory.failed'), {variant});
    }
  };

  return (
    <Container fixed className="serviceInfo__serviceHistory">
      <Grid container spacing={1}>
        {/** タイトル、説明 */}
        <Grid item xs={12}>
          <h1>{trans('ServiceHistory.title')}</h1>
        </Grid>
        {/* テキストフィールド */}
        <Grid item xs={12}>
          <TextField
            multiline
            rows={15}
            value={serviceHistory}
            error={isOver}
            helperText={trans('ServiceHistory.error', {
              num: limit.SITE_INFO_NUM_SERVICE_HISTORY_TEXT_MAX / 1024,
            })}
            FormHelperTextProps={{
              classes: {root: 'serviceInfo__serviceHistory__helperText'},
            }}
            onChange={event => setServiceHistory(event.target.value)}
            fullWidth
          />
        </Grid>
        {/* セーブボタン */}
        <Grid item xs={12} className="serviceInfo__serviceHistory__saveButton">
          <PrimaryButton
            onClick={saveSiteInfo}
            buttonText={trans('ServiceHistory.save')}
            disabled={isOver}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

/**
 * サービスヒストリー機能
 * @returns JSX
 */
const ServiceHistory = () => {
  //SnackbarProviderはuseSnackbar()と同じコンポーネントにできないため分割した
  return (
    <SnackbarProvider maxSnack={3}>
      <Main />
    </SnackbarProvider>
  );
};
export default ServiceHistory;
